import React, { useState, useEffect, useRef } from 'react';
import { X, Download, Loader2, MessageCircle } from 'lucide-react';
import { toast } from 'react-toastify';
import { getPayslip } from '../../api/payroll';
import DownloadFormatModal from '../wholesale/DownloadFormatModal';
import PayslipWhatsAppModal from './PayslipWhatsAppModal';

// Get API URL from environment
const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

/**
 * PayslipDetailModal Component
 * Renders the payslip PDF as high-quality preview images using PDF.js
 * Exactly like ReceiptPreviewModal in InvoicesPage
 */
const PayslipDetailModal = ({ payslipId, isOpen, onClose, module = 'retail' }) => {
  const [payslip, setPayslip] = useState(null);
  const [pageImageUrls, setPageImageUrls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);

  const pdfLibRef = useRef(null); // Cache PDF.js library
  const [whatsAppModalOpen, setWhatsAppModalOpen] = useState(false);
  const [formatModalOpen, setFormatModalOpen] = useState(false);

  useEffect(() => {
    if (payslipId && isOpen) {
      fetchPayslipData();
    }
  }, [payslipId, isOpen]);

  const fetchPayslipData = async () => {
    setLoading(true);
    setLoadError(null);
    setPageImageUrls([]);
    try {
      // 1. Fetch payslip basic details
      const data = await getPayslip(payslipId);
      setPayslip(data);

      // 2. Fetch PDF blob and render pages
      const pdfResponse = await fetch(`${API_URL}/payroll/payslips/${payslipId}/pdf`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`
        }
      });

      if (!pdfResponse.ok) {
        throw new Error(`Failed to fetch PDF: ${pdfResponse.statusText}`);
      }

      const blob = await pdfResponse.blob();
      if (!blob || blob.size === 0) {
        throw new Error('Empty PDF received from server');
      }

      // Dynamic import PDF.js with CDN worker configuration
      if (!pdfLibRef.current) {
        const pdfjsLib = await import('pdfjs-dist');
        pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;
        pdfLibRef.current = pdfjsLib;
      }
      const pdfjsLib = pdfLibRef.current;

      const arrayBuffer = await blob.arrayBuffer();
      const loadingTask = pdfjsLib.getDocument({
        data: arrayBuffer,
        isEvalSupported: false,
        useSystemFonts: true
      });
      const pdfDoc = await loadingTask.promise;
      const numPages = pdfDoc.numPages;

      const scale = 1.2;
      const pageUrls = [];

      for (let pageNum = 1; pageNum <= numPages; pageNum++) {
        const page = await pdfDoc.getPage(pageNum);
        const viewport = page.getViewport({ scale });

        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        canvas.height = viewport.height;
        canvas.width = viewport.width;

        const renderContext = {
          canvasContext: context,
          viewport: viewport
        };

        await page.render(renderContext).promise;

        // Convert canvas to image URL
        const imageBlob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.9));
        const imageUrl = URL.createObjectURL(imageBlob);
        pageUrls.push(imageUrl);
      }

      setPageImageUrls(pageUrls);
      await pdfDoc.destroy();
    } catch (error) {
      console.error('Error rendering payslip:', error);
      setLoadError(error.message || 'Failed to load payslip preview');
      toast.error('Failed to load payslip: ' + (error.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    setFormatModalOpen(true);
  };

  const handleFormatSelected = async (format) => {
    setActionLoading('download');
    try {
      const response = await fetch(`${API_URL}/payroll/payslips/${payslipId}/pdf`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`
        }
      });
      if (!response.ok) throw new Error('Failed to fetch PDF');
      const pdfBlob = (await response.ok) ? await response.blob() : null;

      if (!pdfBlob) throw new Error('Could not download payslip PDF');

      const payslipNumber = payslip?.payslip_number || `payslip-${payslipId}`;

      if (format === 'pdf') {
        const url = window.URL.createObjectURL(pdfBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${payslipNumber}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        toast.success('Payslip downloaded as PDF');
      } else {
        // Render first page as image and download
        if (!pdfLibRef.current) {
          const pdfjsLib = await import('pdfjs-dist');
          pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;
          pdfLibRef.current = pdfjsLib;
        }
        const pdfjsLib = pdfLibRef.current;

        const arrayBuffer = await pdfBlob.arrayBuffer();
        const pdfDoc = await pdfjsLib.getDocument({
          data: arrayBuffer,
          isEvalSupported: false,
          useSystemFonts: true
        }).promise;
        const page = await pdfDoc.getPage(1);

        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        const viewport = page.getViewport({ scale: 2.0 });
        canvas.height = viewport.height;
        canvas.width = viewport.width;

        await page.render({ canvasContext: context, viewport }).promise;

        const imageBlob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
        const url = window.URL.createObjectURL(imageBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${payslipNumber}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        toast.success('Payslip downloaded as PNG Image');
        await pdfDoc.destroy();
      }
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download payslip');
    } finally {
      setActionLoading(null);
    }
  };

  const generateWhatsAppImage = async () => {
    const response = await fetch(`${API_URL}/payroll/payslips/${payslipId}/pdf`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('accessToken')}`
      }
    });
    if (!response.ok) throw new Error('Failed to fetch PDF');
    const pdfBlob = await response.blob();

    if (!pdfLibRef.current) {
      const pdfjsLib = await import('pdfjs-dist');
      pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;
      pdfLibRef.current = pdfjsLib;
    }
    const pdfjsLib = pdfLibRef.current;

    const arrayBuffer = await pdfBlob.arrayBuffer();
    const pdfDoc = await pdfjsLib.getDocument({
      data: arrayBuffer,
      isEvalSupported: false,
      useSystemFonts: true
    }).promise;
    const page = await pdfDoc.getPage(1);

    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    const viewport = page.getViewport({ scale: 2.0 });
    canvas.height = viewport.height;
    canvas.width = viewport.width;

    await page.render({ canvasContext: context, viewport }).promise;
    const imageBlob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
    await pdfDoc.destroy();
    return imageBlob;
  };

  if (!isOpen) return null;

  const payslipNumber = payslip?.payslip_number || 'N/A';

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <div>
              <h2 className="text-lg font-bold text-gray-900">Payslip Details</h2>
              {payslip && (
                <p className="text-sm text-gray-600">
                  {payslipNumber} • {payslip.employee_name}
                </p>
              )}
            </div>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition">
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-auto p-4 bg-gray-50 flex justify-center items-start">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-20 w-full">
                <Loader2 className="w-8 h-8 text-accent-gold animate-spin mb-3" />
                <p className="text-sm text-gray-500 font-medium">Generating payslip preview...</p>
              </div>
            ) : pageImageUrls.length > 0 ? (
              <div className="flex flex-col items-center gap-4 w-full">
                {pageImageUrls.map((url, index) => (
                  <div
                    key={index}
                    className="relative shadow-md rounded-lg border border-gray-200 overflow-hidden bg-white max-w-full"
                  >
                    {pageImageUrls.length > 1 && (
                      <div className="absolute top-2 right-2 bg-black/50 text-white text-xs px-2 py-1 rounded z-10">
                        Page {index + 1} of {pageImageUrls.length}
                      </div>
                    )}
                    <img
                      src={url}
                      alt={`Payslip ${payslipNumber} - Page ${index + 1}`}
                      className="max-w-full h-auto"
                    />
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-20 text-gray-600 w-full">
                <p className="mb-2 font-semibold">Failed to render payslip preview</p>
                {loadError && <p className="text-xs text-red-500 mb-3">{loadError}</p>}
                <button
                  onClick={fetchPayslipData}
                  className="px-4 py-2 text-sm bg-accent-gold text-primary-black font-semibold rounded-lg hover:bg-accent-goldDark transition"
                >
                  Retry
                </button>
              </div>
            )}
          </div>

          {/* Footer actions */}
          <div className="p-4 border-t border-gray-200 bg-white">
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => setWhatsAppModalOpen(true)}
                disabled={loading || pageImageUrls.length === 0}
                className="flex items-center justify-center gap-2 px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition font-semibold disabled:opacity-50"
              >
                <MessageCircle className="w-4 h-4" />
                <span className="text-sm">Send via WhatsApp</span>
              </button>
              <button
                onClick={handleDownload}
                disabled={loading || actionLoading === 'download'}
                className="flex items-center justify-center gap-2 px-4 py-2.5 bg-accent-gold text-primary-black rounded-lg hover:bg-accent-goldDark transition font-semibold disabled:opacity-50"
              >
                {actionLoading === 'download' ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Download className="w-4 h-4" />
                )}
                <span className="text-sm">Download</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* WhatsApp Sharing */}
      {whatsAppModalOpen && payslip && (
        <PayslipWhatsAppModal
          payslip={payslip}
          module={module}
          onClose={() => setWhatsAppModalOpen(false)}
          generateImage={generateWhatsAppImage}
        />
      )}

      {/* Download Dialog */}
      <DownloadFormatModal
        isOpen={formatModalOpen}
        onClose={() => setFormatModalOpen(false)}
        onSelectFormat={handleFormatSelected}
        documentName={`Payslip-${payslipNumber}`}
      />
    </>
  );
};

export default PayslipDetailModal;
